import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type Dispatch,
  type SetStateAction,
} from 'react';

export function useHydratedArrayState<T>(
  sourceItems: readonly T[],
  cloneItem: (item: T) => T = (item) => item,
  onLocalChange?: (items: T[]) => void,
): [T[], Dispatch<SetStateAction<T[]>>] {
  const [items, setItemsState] = useState<T[]>(() => sourceItems.map(cloneItem));
  const [hasLocalEdits, setHasLocalEdits] = useState(false);
  const itemsRef = useRef(items);
  const onLocalChangeRef = useRef(onLocalChange);
  itemsRef.current = items;
  onLocalChangeRef.current = onLocalChange;

  useEffect(() => {
    if (hasLocalEdits || sourceItems.length === 0) return;
    setItemsState((current) => {
      if (current.length > 0) return current;
      const hydrated = sourceItems.map(cloneItem);
      itemsRef.current = hydrated;
      return hydrated;
    });
  }, [cloneItem, hasLocalEdits, sourceItems]);

  const setItems = useCallback<Dispatch<SetStateAction<T[]>>>((next) => {
    const resolved =
      typeof next === 'function' ? next(itemsRef.current) : next;
    itemsRef.current = resolved;
    setHasLocalEdits(true);
    setItemsState(resolved);
    onLocalChangeRef.current?.(resolved);
  }, []);

  return [items, setItems];
}

export function useHydratedValueState<T>(
  sourceValue: T,
  isEmpty: (value: T) => boolean,
): [T, Dispatch<SetStateAction<T>>] {
  const [value, setValueState] = useState<T>(sourceValue);
  const [hasLocalEdits, setHasLocalEdits] = useState(false);

  useEffect(() => {
    if (hasLocalEdits || isEmpty(sourceValue)) return;
    setValueState((current) => {
      if (!isEmpty(current)) return current;
      return sourceValue;
    });
  }, [hasLocalEdits, isEmpty, sourceValue]);

  const setValue = useCallback<Dispatch<SetStateAction<T>>>((next) => {
    setHasLocalEdits(true);
    setValueState(next);
  }, []);

  return [value, setValue];
}
